/**
 * Time Parser Utility Functions
 * Converts ISO 8601 timestamps to user-configured timezone and format
 */

import type {
  UserDateFormat,
  UserLocalePreferences,
  UserTimeFormat,
  UserTimezone,
} from "@/services/userLocalePreferences";

import { logger } from "./logger";

export interface TimezoneSettings {
  timezone: string;
  dateFormat: string;
  timeFormat: string;
}

export interface LocaleEnumSettings {
  timezone?: string;
  dateFormat?: string;
  timeFormat?: string;
}

export interface LocaleDisplaySettings {
  timezone?: string;
  dateFormat?: string;
  timeFormat?: string;
}

const timezoneMap: Record<string, string> = {
  "Pacific Time (US & Canada)": "America/Los_Angeles",
  "Mountain Time (US & Canada)": "America/Denver",
  "Central Time (US & Canada)": "America/Chicago",
  "Eastern Time (US & Canada)": "America/New_York",
  "Philippine Time": "Asia/Manila",
};

const formatDateWithSeparator = (date: Date, dateFormat: string, timezoneId: string): string => {
  const targetDate = new Date(date.toLocaleString("en-US", { timeZone: timezoneId }));
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, "0");
  const day = String(targetDate.getDate()).padStart(2, "0");

  switch (dateFormat) {
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "YYYY/MM/DD":
      return `${year}/${month}/${day}`;
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "D MMM YYYY":
      return date.toLocaleDateString("en-US", {
        timeZone: timezoneId,
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    default:
      return `${month}/${day}/${year}`;
  }
};


const timeFormatMap: Record<string, { hour12: boolean; includeSeconds: boolean }> = {
  "12-hour (1:45 PM)": { hour12: true, includeSeconds: false },
  "12-hour with seconds (1:45:30 PM)": { hour12: true, includeSeconds: true },
  "24-hour (13:45)": { hour12: false, includeSeconds: false },
  "24-hour with seconds (13:45:30)": { hour12: false, includeSeconds: true },
};

export const DEFAULT_TIMEZONE_SETTINGS: TimezoneSettings = {
  timezone: "Pacific Time (US & Canada)",
  dateFormat: "MM/DD/YYYY",
  timeFormat: "12-hour (1:45 PM)",
};

export const enumToTimezoneMap: Record<string, TimezoneSettings["timezone"]> = {
  PACIFIC: "Pacific Time (US & Canada)",
  MOUNTAIN: "Mountain Time (US & Canada)",
  CENTRAL: "Central Time (US & Canada)",
  EASTERN: "Eastern Time (US & Canada)",
};

export const enumToDateFormatMap: Record<string, TimezoneSettings["dateFormat"]> = {
  MM_DD_YYYY: "MM/DD/YYYY",
  DD_MM_YYYY: "DD/MM/YYYY",
  YYYY_MM_DD_SLASH: "YYYY/MM/DD",
  YYYY_MM_DD_DASH: "YYYY-MM-DD",
  D_MMM_YYYY: "D MMM YYYY",
};

export const enumToTimeFormatMap: Record<string, TimezoneSettings["timeFormat"]> = {
  H12: "12-hour (1:45 PM)",
  H12_SECONDS: "12-hour with seconds (1:45:30 PM)",
  H24: "24-hour (13:45)",
  H24_SECONDS: "24-hour with seconds (13:45:30)",
};

export const timezoneToEnumMap: Record<string, UserTimezone> = {
  "Pacific Time (US & Canada)": "PACIFIC",
  "Mountain Time (US & Canada)": "MOUNTAIN",
  "Central Time (US & Canada)": "CENTRAL",
  "Eastern Time (US & Canada)": "EASTERN",
};

export const dateFormatToEnumMap: Record<string, UserDateFormat> = {
  "MM/DD/YYYY": "MM_DD_YYYY",
  "DD/MM/YYYY": "DD_MM_YYYY",
  "YYYY/MM/DD": "YYYY_MM_DD_SLASH",
  "YYYY-MM-DD": "YYYY_MM_DD_DASH",
  "D MMM YYYY": "D_MMM_YYYY",
};

export const timeFormatToEnumMap: Record<string, UserTimeFormat> = {
  "12-hour (1:45 PM)": "H12",
  "12-hour with seconds (1:45:30 PM)": "H12_SECONDS",
  "24-hour (13:45)": "H24",
  "24-hour with seconds (13:45:30)": "H24_SECONDS",
};

export const mapLocaleSettingsToDisplay = (
  settings?: LocaleEnumSettings | Partial<TimezoneSettings>
): TimezoneSettings => {
  if (!settings) {
    return { ...DEFAULT_TIMEZONE_SETTINGS };
  }

  return {
    timezone:
      enumToTimezoneMap[settings.timezone || ""] ||
      settings.timezone ||
      DEFAULT_TIMEZONE_SETTINGS.timezone,
    dateFormat:
      enumToDateFormatMap[settings.dateFormat || ""] ||
      settings.dateFormat ||
      DEFAULT_TIMEZONE_SETTINGS.dateFormat,
    timeFormat:
      enumToTimeFormatMap[settings.timeFormat || ""] ||
      settings.timeFormat ||
      DEFAULT_TIMEZONE_SETTINGS.timeFormat,
  };
};

export const mapLocaleSettingsToEnum = (
  settings?: LocaleDisplaySettings
): UserLocalePreferences => {
  const displaySettings = mapLocaleSettingsToDisplay(settings);

  return {
    timezone: timezoneToEnumMap[displaySettings.timezone] || "PACIFIC",
    dateFormat: dateFormatToEnumMap[displaySettings.dateFormat] || "MM_DD_YYYY",
    timeFormat: timeFormatToEnumMap[displaySettings.timeFormat] || "H12",
  };
};

const resolveTimezoneSettings = (): TimezoneSettings => {
  try {
    const raw = localStorage.getItem("timezoneSettings");
    if (!raw) {
      return DEFAULT_TIMEZONE_SETTINGS;
    }

    const parsed = JSON.parse(raw) as LocaleEnumSettings;
    return mapLocaleSettingsToDisplay(parsed);
  } catch {
    return DEFAULT_TIMEZONE_SETTINGS;
  }
};

export const getUserTodayISO = (): string => {
  const settings = resolveTimezoneSettings();
  const timezoneId = timezoneMap[settings.timezone] || "America/Los_Angeles";
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezoneId,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const year = parts.find((p) => p.type === "year")!.value;
  const month = parts.find((p) => p.type === "month")!.value;
  const day = parts.find((p) => p.type === "day")!.value;
  return `${year}-${month}-${day}`;
};

export const parseToUserTime = (
  isoString: string,
  settings?: Partial<TimezoneSettings>
): string => {
  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }

    const savedSettings = resolveTimezoneSettings();
    const finalSettings = { ...DEFAULT_TIMEZONE_SETTINGS, ...savedSettings, ...settings };
    const timezoneId = timezoneMap[finalSettings.timezone] || "America/Los_Angeles";
    
    return formatDateTime(date, finalSettings, timezoneId);

  } catch (error) {
    logger.error("Error parsing time", { error, context: "parseTime" });
    return "Invalid date";
  }
};

export const parseToUserDate = (
  isoString: string,
  settings?: Partial<TimezoneSettings>
): string => {
  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }

    const savedSettings = resolveTimezoneSettings();
    const finalSettings = { ...DEFAULT_TIMEZONE_SETTINGS, ...savedSettings, ...settings };
    const timezoneId = timezoneMap[finalSettings.timezone] || "America/Los_Angeles";

    return formatDateWithSeparator(date, finalSettings.dateFormat, timezoneId);
  } catch (error) {
    logger.error("Error parsing date", { error, context: "parseToUserDate" });
    return "Invalid date";
  }
};

const formatDateTime = (date: Date, settings: TimezoneSettings, timezoneId: string): string => {
  const timeOptions = timeFormatMap[settings.timeFormat] || timeFormatMap["12-hour (1:45 PM)"];
  const datePart = formatDateWithSeparator(date, settings.dateFormat, timezoneId);

  const timeOptionsIntl: Intl.DateTimeFormatOptions = {
    timeZone: timezoneId,
    hour: "2-digit",
    minute: "2-digit",
    hour12: timeOptions.hour12,
  };

  if (timeOptions.includeSeconds) {
    timeOptionsIntl.second = "2-digit";
  }

  const timePart = date.toLocaleTimeString("en-US", timeOptionsIntl);
  return `${datePart} ${timePart}`;
};
